import { FC, useState, useMemo, useRef, useCallback } from 'react';
import { Checkbox, CalendarProps, ConfiguredCalendar, CloseIcon, Calendar } from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { useBundleFilterOptions } from '../hooks';
import { classNames } from 'primereact/utils';
import { isProvidersFilter } from '@/ui/pages/BundlePage/utils';
import {
  FILTER_BLANKS_KEY,
  FILTER_NON_BLANKS_KEY,
  FILTER_EXTRACTED_KEY,
  FILTER_DUPLICATES_KEY,
} from '@/constants/filters';
import {
  selectSharedDataActiveBundleType,
  useAppSelector,
  useAppDispatch,
  BundleType,
  selectLastAppliedDateEnd,
  setLastAppliedDateEnd,
} from '@/store';

const capitalizeFirstLetter = (str: string) => {
  return String(str).charAt(0).toUpperCase() + String(str).slice(1);
};

interface FilterDialogProps {
  onHide: (isVisible: boolean) => void;
}

export const FilterDialog: FC<FilterDialogProps> = ({ onHide }) => {
  const {
    sortedBundleFilterOptions,
    localSelectedFilters,
    localSelectedValues,
    localEnabledStates,
    handleCheckboxChange,
    handleValueChange,
    handleDeselectAllFilters,
    handleResetLocalState,
    handleApply,
  } = useBundleFilterOptions();

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const isFullBundle = activeBundleType === BundleType.FULL;
  const lastAppliedDateEndIso = useAppSelector(selectLastAppliedDateEnd);
  const dispatch = useAppDispatch();

  // Static boolean filters (not from BE filterOptions)
  const staticFilters = useMemo(() => {
    const filters = [
      { key: FILTER_BLANKS_KEY, label: 'Include blank pages' },
      { key: FILTER_NON_BLANKS_KEY, label: 'Show only blank pages' },
      { key: FILTER_DUPLICATES_KEY, label: 'Hide duplicate pages' },
    ];
    if (isFullBundle) {
      filters.push({ key: FILTER_EXTRACTED_KEY, label: 'Hide extracted pages' });
    }
    return filters;
  }, [isFullBundle]);

  const [activeGroupName, setActiveGroupName] = useState<string>(sortedBundleFilterOptions[0]?.groupName ?? '');

  const [searchQuery, setSearchQuery] = useState('');

  // Drag state (no persistence — resets on close)
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button')) return;
      isDraggingRef.current = true;
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!isDraggingRef.current || !dragStartRef.current) return;
        setDragOffset({
          x: dragStartRef.current.offsetX + (ev.clientX - dragStartRef.current.mouseX),
          y: dragStartRef.current.offsetY + (ev.clientY - dragStartRef.current.mouseY),
        });
      };
      const handleMouseUp = () => {
        isDraggingRef.current = false;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      e.preventDefault();
    },
    [dragOffset],
  );

  const isSearching = searchQuery.trim().length > 0;

  // Find the active filter group
  const activeGroup = useMemo(
    () => sortedBundleFilterOptions.find((opt) => opt.groupName === activeGroupName),
    [sortedBundleFilterOptions, activeGroupName],
  );

  // Build flat search results across all searchable groups (exclude dates and providers)
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const query = searchQuery.toLowerCase();
    const results: Array<{ groupName: string; optionId: string; optionLabel: string }> = [];

    for (const group of sortedBundleFilterOptions) {
      if (group.displayType === 'dates') continue;

      if (group.displayType === 'drop_down') {
        const isProviders = isProvidersFilter(group);
        const opts = isProviders
          ? (group.options as unknown as Array<{ providerId: string; providerName: string }>).map((opt) => ({
              id: opt.providerId,
              label: opt.providerName,
            }))
          : group.options.map((opt) => ({ id: opt.id, label: opt.value }));
        for (const opt of opts) {
          if (opt.label.toLowerCase().includes(query)) {
            results.push({ groupName: group.groupName, optionId: opt.id, optionLabel: opt.label });
          }
        }
      } else if (group.displayType === 'checkbox') {
        for (const opt of group.options) {
          if (opt.value.toLowerCase().includes(query)) {
            results.push({ groupName: group.groupName, optionId: opt.id, optionLabel: opt.value });
          }
        }
      }
    }

    // Include static boolean filters in search
    for (const sf of staticFilters) {
      if (sf.label.toLowerCase().includes(query)) {
        results.push({ groupName: '__pages__', optionId: sf.key, optionLabel: sf.label });
      }
    }

    return results;
  }, [isSearching, searchQuery, sortedBundleFilterOptions, staticFilters]);

  // Check if a group has active filters
  const hasActiveFilters = (groupName: string): boolean => {
    const group = sortedBundleFilterOptions.find((opt) => opt.groupName === groupName);
    if (!group) return false;

    if (group.displayType === 'dates') {
      return (
        (localEnabledStates[groupName] ?? false) &&
        Array.isArray(localSelectedValues[groupName]) &&
        (localSelectedValues[groupName] as Date[]).some(Boolean)
      );
    }
    if (group.displayType === 'drop_down') {
      return (
        (localEnabledStates[groupName] ?? false) &&
        Array.isArray(localSelectedValues[groupName]) &&
        localSelectedValues[groupName].length > 0
      );
    }
    if (group.displayType === 'checkbox') {
      return Array.isArray(localSelectedValues[groupName]) && localSelectedValues[groupName].length > 0;
    }
    return false;
  };

  // Toggle a search result checkbox
  const toggleSearchResult = (groupName: string, optionId: string, checked: boolean) => {
    // Handle static boolean filters
    if (groupName === '__pages__') {
      handleCheckboxChange(optionId);
      return;
    }

    const currentValues = (localSelectedValues[groupName] as string[]) ?? [];
    const newValues = checked ? [...currentValues, optionId] : currentValues.filter((id: string) => id !== optionId);
    handleValueChange(groupName, newValues);

    // Auto-enable/disable the group for drop_down filters
    const group = sortedBundleFilterOptions.find((opt) => opt.groupName === groupName);
    if (group?.displayType === 'drop_down') {
      const isEnabled = localEnabledStates[groupName] ?? false;
      if (checked && !isEnabled) {
        handleCheckboxChange(groupName);
      } else if (!checked && newValues.length === 0 && isEnabled) {
        handleCheckboxChange(groupName);
      }
    }
  };

  // Render search results as flat list
  const renderSearchResults = () => (
    <div className="flex flex-col gap-4 overflow-y-auto">
      {searchResults.length === 0 ? (
        <span className="text-fletchers-gray-175 text-[14px]">No results found</span>
      ) : (
        searchResults.map((result) => {
          const isStatic = result.groupName === '__pages__';
          const isChecked = isStatic
            ? (localEnabledStates[result.optionId] ?? false)
            : ((localSelectedValues[result.groupName] as string[]) ?? []).includes(result.optionId);
          return (
            <label key={`${result.groupName}-${result.optionId}`} className="flex items-center gap-4">
              <Checkbox
                checked={isChecked}
                onChange={(e) => toggleSearchResult(result.groupName, result.optionId, e.checked ?? false)}
              />
              <div className="flex flex-col">
                <span className="text-[14px] leading-[20px] text-black">{result.optionLabel}</span>
                <span className="text-fletchers-gray-175 text-[11px] leading-[14px]">
                  {isStatic ? 'Page tools' : capitalizeFirstLetter(result.groupName)}
                </span>
              </div>
            </label>
          );
        })
      )}
    </div>
  );

  // Render the right-side content based on the active group's displayType
  const renderContent = () => {
    // Handle static boolean filters grouped as "Pages"
    if (activeGroupName === '__pages__') {
      return (
        <div className="flex flex-col gap-4">
          {staticFilters.map((sf) => (
            <label key={sf.key} className="flex items-center gap-4">
              <Checkbox checked={localEnabledStates[sf.key] ?? false} onChange={() => handleCheckboxChange(sf.key)} />
              <span className="text-[14px] leading-[20px] text-black">{sf.label}</span>
            </label>
          ))}
        </div>
      );
    }

    if (!activeGroup) return null;

    switch (activeGroup.displayType) {
      case 'dates': {
        const isEnabled = localEnabledStates[activeGroup.groupName] ?? false;
        const handleDateChange = (event: CalendarProps<'single', Date | (Date | null)[]>) => {
          const dates = (event.value as Date[]) ?? [];
          const hasAnyDate = dates.some(Boolean);
          // Keep the enabled state in sync with whether any date is selected so the
          // filter can't end up enabled-but-empty (or disabled-but-with-dates).
          // handleCheckboxChange toggles, so only call it when the states disagree.
          if (hasAnyDate !== isEnabled) {
            handleCheckboxChange(activeGroup.groupName);
          }
          handleValueChange(activeGroup.groupName, dates);
          // Remember the end of this range so reopening the calendar lands on the
          // same month even after the user clears/applies. Scoped per BundleType in
          // Redux (cleared on case/bundle change) so it can't leak.
          const end = dates[1] || dates[0];
          if (end && activeBundleType) {
            dispatch(setLastAppliedDateEnd({ key: activeBundleType, data: end.toISOString() }));
          }
        };
        // Calendar default month: prefer the end of the currently-selected range
        // (reflects in-flight edits), then the remembered last end (survives clear
        // + apply within the same bundle), then fall back to one year ago.
        const currentDateVals = localSelectedValues[activeGroup.groupName] as Date[] | undefined;
        const rememberedEnd = lastAppliedDateEndIso ? new Date(lastAppliedDateEndIso) : null;
        const defaultViewDate =
          currentDateVals?.[1] ||
          currentDateVals?.[0] ||
          rememberedEnd ||
          (() => {
            const d = new Date();
            d.setFullYear(d.getFullYear() - 1);
            return d;
          })();
        return (
          <div className="flex flex-col">
            <div className="relative">
              <ConfiguredCalendar
                key={(localSelectedValues[activeGroup.groupName] as Date[])?.length ? 'has-dates' : 'no-dates'}
                value={
                  (localSelectedValues[activeGroup.groupName] as Date[])?.length
                    ? (localSelectedValues[activeGroup.groupName] as Date[])
                    : null
                }
                minDate={new Date(activeGroup.startDate)}
                maxDate={new Date(activeGroup.endDate)}
                selectionMode="range"
                readOnlyInput={false}
                hideOnRangeSelection
                showIcon={false}
                onChange={handleDateChange}
                className="!h-[32px] w-full !text-[14px] [&_input]:!pr-8 [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
              />
              {Array.isArray(localSelectedValues[activeGroup.groupName]) &&
                (localSelectedValues[activeGroup.groupName] as Date[]).some(Boolean) && (
                  <button
                    type="button"
                    className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => {
                      // Clear values and disable the date filter
                      handleValueChange(activeGroup.groupName, []);
                      // handleCheckboxChange toggles, so call it once if enabled to disable
                      handleCheckboxChange(activeGroup.groupName);
                    }}
                  >
                    <CloseIcon width={10} height={10} />
                  </button>
                )}
            </div>
            <Calendar
              value={
                (localSelectedValues[activeGroup.groupName] as Date[])?.length
                  ? (localSelectedValues[activeGroup.groupName] as Date[])
                  : null
              }
              viewDate={defaultViewDate}
              minDate={new Date(activeGroup.startDate)}
              maxDate={new Date(activeGroup.endDate)}
              selectionMode="range"
              onChange={handleDateChange}
              inline
              showIcon={false}
              dateFormat="dd/mm/yy"
              className="w-full [&_.p-datepicker-calendar-container]:!px-0 [&_.p-datepicker-calendar-container]:!py-0 [&_.p-datepicker-header]:!px-0 [&_.p-datepicker-header]:!pt-0 [&_.p-datepicker-header]:!pb-4 [&_[data-pc-section]]:!px-0 [&_table]:!w-full [&_td]:!p-0 [&>div]:!w-full [&>div]:!border-0 [&>div]:!px-0 [&>div]:!py-0"
              pt={{ dayLabel: { style: { height: '30px', width: '100%', margin: 0 } } }}
            />
          </div>
        );
      }

      case 'drop_down': {
        const isEnabled = localEnabledStates[activeGroup.groupName] ?? false;
        const isProviders = isProvidersFilter(activeGroup);
        const options = isProviders
          ? (activeGroup.options as unknown as Array<{ providerId: string; providerName: string }>).map((opt) => ({
              id: opt.providerId,
              label: opt.providerName,
            }))
          : activeGroup.options.map((opt) => ({ id: opt.id, label: opt.value }));

        const selectedIds = (localSelectedValues[activeGroup.groupName] as string[]) ?? [];

        const handleDropdownChange = (optId: string, checked: boolean) => {
          const newValues = checked ? [...selectedIds, optId] : selectedIds.filter((id: string) => id !== optId);
          handleValueChange(activeGroup.groupName, newValues);
          // Auto-enable when selecting, auto-disable when all cleared
          if (checked && !isEnabled) {
            handleCheckboxChange(activeGroup.groupName);
          } else if (!checked && newValues.length === 0 && isEnabled) {
            handleCheckboxChange(activeGroup.groupName);
          }
        };

        return (
          <div className="flex flex-col gap-4">
            {options.map((opt) => (
              <label key={opt.id} className="flex items-center gap-4">
                <Checkbox
                  checked={selectedIds.includes(opt.id)}
                  onChange={(e) => handleDropdownChange(opt.id, e.checked ?? false)}
                />
                <span className="text-[14px] leading-[20px] text-black">{opt.label}</span>
              </label>
            ))}
          </div>
        );
      }

      case 'checkbox': {
        const selectedIds = (localSelectedValues[activeGroup.groupName] as string[]) ?? [];

        return (
          <div className="flex flex-col gap-4">
            {activeGroup.options.map((opt) => (
              <label key={opt.id} className="flex items-center gap-4">
                <Checkbox
                  checked={selectedIds.includes(opt.id)}
                  onChange={(e) =>
                    handleValueChange(
                      activeGroup.groupName,
                      e.checked ? [...selectedIds, opt.id] : selectedIds.filter((id: string) => id !== opt.id),
                    )
                  }
                />
                <span className="text-[14px] leading-[20px] text-black">{opt.value}</span>
              </label>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {/* Click-away backdrop (no dark bg) */}
      <div
        className="fixed inset-0 z-[98]"
        onClick={() => {
          handleResetLocalState();
          onHide(false);
        }}
      />

      {/* Filter modal */}
      <div
        className="absolute top-full right-0 z-[99] mt-2 flex flex-col rounded-lg bg-white"
        style={{
          width: '600px',
          height: '480px',
          border: '1px solid #E1E1E0',
          boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="border-fletchers-gray-110 flex cursor-grab items-center justify-between border-b px-[24px] py-[16px] select-none active:cursor-grabbing"
          onMouseDown={handleDragStart}
        >
          <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Filters</div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              handleResetLocalState();
              onHide(false);
            }}
            className="cursor-pointer rounded p-1 focus:outline-none"
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col items-end gap-[24px] p-[24px]">
          {/* Body: sidebar + divider + content */}
          <div className="flex min-h-0 w-full flex-1">
            {/* Left sidebar — search (fixed) + scrollable filter group names */}
            <div className="flex min-h-0 flex-col pr-[24px]">
              {/* Global search — fixed at top */}
              <div className="relative mb-[16px] shrink-0">
                <i className="pi pi-search absolute top-1/2 left-[10px] -translate-y-1/2 text-[14px] text-gray-400" />
                <input
                  type="text"
                  placeholder="Search filters"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={classNames(
                    'border-fletchers-gray-110 h-[32px] w-full rounded-[4px] border bg-white pr-8 pl-[35px] text-[14px] leading-[20px] text-black',
                    'placeholder:text-fletchers-gray-200 focus:outline-none',
                  )}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => setSearchQuery('')}
                  >
                    <CloseIcon width={10} height={10} />
                  </button>
                )}
              </div>

              {/* Scrollable filter list */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {/* Sidebar items: dates → page tools → providers → rest */}
                {(() => {
                  const dates = sortedBundleFilterOptions.filter((o) => o.displayType === 'dates');
                  const providers = sortedBundleFilterOptions.filter((o) => isProvidersFilter(o));
                  const rest = sortedBundleFilterOptions.filter(
                    (o) => o.displayType !== 'dates' && !isProvidersFilter(o),
                  );

                  const renderSidebarButton = (key: string, label: string, showDot: boolean) => {
                    const isActive = !isSearching && activeGroupName === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setActiveGroupName(key);
                          setSearchQuery('');
                        }}
                        className={classNames(
                          'flex h-[32px] min-h-[32px] cursor-pointer items-center justify-between rounded-[4px] px-3 text-left text-[14px] font-medium text-black',
                          'focus:outline-none',
                          'border-l-2 border-l-transparent',
                          'hover:bg-fletchers-gray-50',
                          {
                            'bg-fletchers-green-50 !border-fletchers-green-300 !text-fletchers-green-500': isActive,
                          },
                        )}
                      >
                        <span className="whitespace-nowrap">{label}</span>
                        {/* Always reserve space for the dot to prevent width changes */}
                        <span
                          className={classNames(
                            'ml-2 h-[4px] w-[4px] shrink-0 rounded-full',
                            showDot ? 'bg-amber-500' : 'bg-transparent',
                          )}
                        />
                      </button>
                    );
                  };

                  const items: React.ReactNode[] = [];

                  // Dates
                  for (const opt of dates) {
                    items.push(
                      renderSidebarButton(
                        opt.groupName,
                        capitalizeFirstLetter(opt.groupName),
                        hasActiveFilters(opt.groupName),
                      ),
                    );
                  }

                  // Page tools
                  if (staticFilters.length > 0) {
                    items.push(
                      renderSidebarButton(
                        '__pages__',
                        'Page tools',
                        staticFilters.some((sf) => localEnabledStates[sf.key]),
                      ),
                    );
                  }

                  // Providers
                  for (const opt of providers) {
                    items.push(
                      renderSidebarButton(
                        opt.groupName,
                        capitalizeFirstLetter(opt.groupName),
                        hasActiveFilters(opt.groupName),
                      ),
                    );
                  }

                  // Rest
                  for (const opt of rest) {
                    items.push(
                      renderSidebarButton(
                        opt.groupName,
                        capitalizeFirstLetter(opt.groupName),
                        hasActiveFilters(opt.groupName),
                      ),
                    );
                  }

                  return items;
                })()}
              </div>
            </div>

            {/* Vertical divider */}
            <div className="border-fletchers-gray-110 border-l" />

            {/* Right content area */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto pl-[24px]">
              {isSearching ? renderSearchResults() : renderContent()}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2">
            <OutlineButton className="!h-[32px] !text-[14px] !font-semibold" onClick={handleDeselectAllFilters}>
              Clear all
            </OutlineButton>
            <GreenButton
              className="!h-[32px] !text-[14px] !font-semibold"
              onClick={() => {
                // Commit the last-picked end date to Redux so it survives clear +
                // apply cycles (the calendar will still reopen on that month).
                const datesGroup = sortedBundleFilterOptions.find((opt) => opt.displayType === 'dates');
                if (datesGroup && activeBundleType) {
                  const vals = localSelectedValues[datesGroup.groupName] as Date[] | undefined;
                  const end = vals?.[1] || vals?.[0];
                  if (end) {
                    dispatch(setLastAppliedDateEnd({ key: activeBundleType, data: end.toISOString() }));
                  }
                }
                handleApply();
                onHide(false);
              }}
            >
              Apply {localSelectedFilters} {localSelectedFilters === 1 ? 'filter' : 'filters'}
            </GreenButton>
          </div>
        </div>
      </div>
    </>
  );
};
